<template>
    <v-container v-resize="onResize">
        <v-layout v-if="!this.loading" row wrap align-center>
            <v-flex xs12 md12 lg4>
                <h1>Royal Commissions</h1>
            </v-flex>
            <v-flex class="pt-xs-2" xs6 sm3 offset-md4 offset-lg0 md2 lg2>
                <count-up :endVal="items.length" text="Royal commissions since federation" />
            </v-flex>
        </v-layout>
        <v-layout v-if="!this.loading" row wrap align-center class="hidden-print-only">
            <v-flex xs12 md3 mt-4 mt-xs-2>
                <div>
                    <v-text-field :class="{mobileTextField: isMobile}" v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
                    <v-menu v-if="!this.loading && this.isMobile" d-inline offset-y :nudge-left="140" :close-on-content-click="true">
                        <v-btn outline small slot="activator">
                            Sort <v-icon>sort</v-icon>
                        </v-btn>
                        <v-list>
                             <v-subheader
                                >
                                Sort by:
                                </v-subheader>
                            <v-list-tile v-for="(item, index) in HEADERS" :key="item.value" @click="changeSort(item.value)">
                                <v-list-tile-title>
                                    {{ item.text }}
                                    <v-icon v-if="PAGINATION.sortBy === item.value">{{PAGINATION.descending ? 'arrow_downward':'arrow_upward'}}</v-icon>
                                </v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </div>
            </v-flex>
        </v-layout>
        <br/>
        <loader v-if="loading" />       

        <chart :chartData="chartData"/>
        <br/>

        <div class="referendums-table"> 
            <v-data-table 
                v-if="!loading"
                :headers="HEADERS"
                :footer-props="FOOTERS"
                :items="items"
                item-key="$id"
                :search="search"
                :rows-per-page-items="ROWS_LISTITEMS"
                :pagination.sync="PAGINATION"
                :hide-headers="isMobile || isPrint"
                :expand="expand"
                :hide-actions="isPrint"
                :disable-initial-sort="true"
                :class="{ mobileDataTable: isMobile || isPrint, 'elevation-1': true }">
                <template v-slot:items="props">
                    <tr v-if="isMobile || isPrint">
                        <td>
                            <div class="flex-content">
                                <div class="flex-item"><h3 class="pb-xs-0">{{ props.item.Title }}</h3></div>
                                <div class="flex-item" data-label="Date of Letters Patent">{{ props.item.DateLettersPatent | stringDateFormat }}</div>
                                <div class="flex-item" data-label="Date Final Report Presented">{{ props.item.DateFinalReportPresented | stringDateFormat }}</div>                                

                                <div v-for="report in props.item.RoyalCommissionReports" :key="report.Id">
                                    <div v-if="report.Link.includes('http')" class="flex-item" :data-label="'Report Part ' + report.Part"><v-btn small target="_blank" :href="report.Link"><v-icon aria-hidden="true">outbound</v-icon></v-btn></div>
                                    <div v-else>{{ report.Link }}</div>
                                </div>

                                <div class="flex-item" v-if="props.item.GovernmentResponse" data-label="Government Response"><v-btn small v-if="props.item.GovernmentResponse" target="_blank" :href="props.item.GovernmentResponse"><v-icon aria-hidden="true">outbound</v-icon></v-btn></div>                                
                            </div>
                        </td>
                    </tr>
                    <tr v-else @click="props.expanded = !props.expanded">
                        <td style="padding-right: 0"><v-icon>{{ props.expanded ? 'expand_less' : 'expand_more' }}</v-icon></td>
                        <td class="text-xs-left font-weight-bold">{{ props.item.Title }}</td>
                        <td class="text-xs-left">{{ props.item.DateLettersPatent | stringDateFormat }}</td>
                        <td class="text-xs-left">{{ props.item.DateFinalReportPresented | stringDateFormat }}</td>                        
                    </tr>
                </template>
                <template v-slot:expand="props">
                    <div class="pa-3">
                        <h3 class="pa-2">Reports</h3>
                        <v-data-table :headers="[{ text: 'Report', value: 'Part'}, {text: 'Link', value: 'Link' }]"
                                      :disable-initial-sort="true"
                                      :hide-actions="true"
                                      :class="{ mobileDataTable: isMobile, 'elevation-1': true }"
                                      :items="props.item.RoyalCommissionReports">
                            <template v-slot:items="propsInner">
                                <tr v-if="propsInner.item.Link.includes('http')">
                                    <td>Report {{ propsInner.item.Part }}</td>
                                    <td><v-btn target="_blank" :href="propsInner.item.Link"><v-icon aria-hidden="true">outbound</v-icon></v-btn></td>
                                </tr>
                                <tr v-else>
                                    <td colspan="2" style="text-align: center;">{{ propsInner.item.Link }}</td>
                                </tr>
                            </template>
                        </v-data-table>

                        <template v-if="props.item.GovernmentResponse">
                        <br/>
                        <h3>Government Response</h3>
                        <v-btn target="_blank" :href="props.item.GovernmentResponse"><v-icon aria-hidden="true">outbound</v-icon></v-btn>
                        </template>
                    </div>
                </template>
            </v-data-table>    
        </div>   
        <br/>
        <v-layout row wrap class="hidden-print-only">
            <v-flex xs12>
                <v-btn class="csvButton" @click="exportCsv" v-on:keyup.enter="exportCsv" color="primary">
                    <v-icon aria-hidden="true" :class="csvDownloadClass">{{csvDownloadIcon}}</v-icon>
                    <span>&nbsp;Download CSV</span>
                </v-btn>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>    
    import print from '../../mixins/print';
    import Loader from '@/components/Loader.vue';
    import axios from 'axios';
    import CountUp from "../StatisticalInformation/CountUp.vue";
    import Chart from '../StatisticalInformation/Chart.vue';
    window.Handbook = window.Handbook || {}
    const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
    axios.defaults.baseURL = apiUri;

    export default {
        created() {
            this.initialiseItems();
            this.onResize();
        },
        mixins: [print],
        components: {
            Loader,
            Chart,
            CountUp
        },
        data() {
            return {
                expand: false,
                isMobile: false,
                search: '',
                items: [],
                referendumsCount: 0,
                referendumsPassedCount: 0,
                plebiscitesCount: 0,
                plebiscitesPassedCount: 0,
                loading: true,
                csvDownloading: false,
                referendumOptionSelected: 0,
                referendumOptions: ['Referendum', 'Plebiscite'],
                ROWS_LISTITEMS: [5, 10, 50, {
                    "text": "$vuetify.dataIterator.rowsPerPageAll",
                    "value": -1
                }],
                PAGINATION: {
                    rowsPerPage: this.$store.state.ROWS_PER_PAGE,
                    sortBy: 'Date',
                    descending: true
                },
                HEADERS: [
                {
                    text: '',
                    value: 'Parliament',
                    sortable: false,
                    width: '10px'
                },
                {
                    text: 'Title',
                    value: 'Title',
                    align: 'left',
                    sortable: true
                },
                {
                    text: 'Date of Letters Patent',
                    value: 'DateLettersPatent',
                    align: 'left',
                    sortable: true,
                },
                {
                    text: 'Date Final Report Presented',
                    value: 'DateFinalReportPresented',
                    align: 'left',
                    sortable: true,
                }
                ],
                FOOTERS: {
                    showFirstLastPage: true,
                    firstIcon: 'mdi-arrow-collapse-left',
                    lastIcon: 'mdi-arrow-collapse-right',
                    prevIcon: 'mdi-minus',
                    nextIcon: 'mdi-plus',
                },
            }
        },
        computed: {
            csvDownloadIcon(){
                return this.csvDownloading ? 'cached' : 'text_snippet';
            },
            csvDownloadClass(){
                let csvClass = 'mr-1';
                if (this.csvDownloading){
                    csvClass = csvClass + ' loading'
                }
                return csvClass;
            },
            chartData() {

                const temp = this.items.map(i => new Date(i.DateLettersPatent).getFullYear());

                let data = [];

                for (let i = 1901; i < new Date().getFullYear() + 1; i++){
                    data.push(temp.filter(d => d === i).length);
                }

                const plotLines = this.$store.getters.GET_PARLIAMENTS_ORDERED
                    .map(p => ({
                        value: (new Date(p.DateElection).getFullYear()).valueOf(),
                        dashStyle: 'shortdash',
                        color: '#CCD6EB',
                        label: {
                            text: p.PID,
                            y: 0
                        }}));

                return {
                chart: {
                    spacing: [10, 20, 15, 10]
                },
                title: {
                    text: 'Number of royal commissions established per year'
                },

                yAxis: {
                    title: {
                        text: 'Royal commissions established'
                    }
                },
                xAxis: [{
                    plotLines,
                },
                {
                    title: {
                        text: 'Parliaments'
                    },
                    offset: 15,
                    lineColor: 'transparent',
                    opposite: true
                }
                ],

                plotOptions: {
                    series: {
                        label: {
                            connectorAllowed: false
                        },
                        pointStart: 1901
                    }
                },
                series: 
                [
                    {
                        name: 'Royal commissions',
                        data
                    },
                ]
                }
            }
        },
        mounted() {
            this.onResize();
            //Set page to 10 on mobile, only on mount
            if (this.isMobile){
                this.PAGINATION.rowsPerPage = 10;
            }
        },
        methods: {
            onResize() {
                if (window.innerWidth < 1060) {
                    this.isMobile = true;
                }
                else {
                    this.isMobile = false;
                }
            },
            changeSort(column) {
                if (this.PAGINATION.sortBy === column) {
                    this.PAGINATION.descending = !this.PAGINATION.descending
                } 
                else {
                    this.PAGINATION.sortBy = column
                    this.PAGINATION.descending = false
                }
            },
            initialiseItems() {
                this.loading = true;
                const url = 'RoyalCommissions/RoyalCommissions';
                axios.get(`api/${url}`)
                    .then(response => {
                        this.items = response.data;
                        this.loading = false;
                    });
            },
            exportCsv() {
                this.$gtag.event('royal commissions download csv', {
                    'event_category': 'downloads',
                    'event_label': `royal commissions csv download`,
                    'value': 1
                });

                this.csvDownloading = true;
                const records = this.items.map(i => 
                    ({ 
                        Title: i.Title,
                        'Date of Letters Patent': i.DateLettersPatent,
                        'Date Final Report Presented': i.DateFinalReportPresented,
                        'Government Response': i.GovernmentResponse
                    }));
                let headers = this.HEADERS.map(h => h.text);
                const filename = 'Royal commissions';
                const comment = `Data current as of ${(new Date()).toLocaleDateString()}`;
                this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename, COMMENT: comment })
                    .catch(this.showError)
                    .finally(() => this.csvDownloading = false);
            }
        },
    }
</script>
<style lang="stylus" scoped>
    .v-progress-circular {
        margin: 1rem
    }

    .v-datatable .tr {
        display:table-row;
    }

    .v-datatable a.tr {
        color: #0e1531de;
        text-decoration:none;
        border: 1px solid #dbdbdb;
        border-left: none;
        border-right: none;
    }

    .v-datatable tbody td {
        vertical-align: middle;
    }
    
    @media screen and (max-width: 1420px) {
        .referendums-table >>> table.v-table thead td:not(:nth-child(1)), 
        .referendums-table >>> table.v-table tbody td:not(:nth-child(1)), 
        .referendums-table >>> table.v-table thead th:not(:nth-child(1)), 
        .referendums-table >>> table.v-table tbody th:not(:nth-child(1)), 
        .referendums-table >>> table.v-table thead td:first-child, 
        .referendums-table >>> table.v-table tbody td:first-child, 
        .referendums-table >>> table.v-table thead th:first-child, 
        .referendums-table >>> table.v-table tbody th:first-child,
        .referendums-table >>> table.v-table thead th:first-child {
            padding: 0 14px !important;
        }

        .referendums-table >>> th {
            white-space: break-spaces;
        }
    }
</style>